import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { createSlot, listSlots, updateSlot } from '../features/covers/api';
import type { MediaSlotRow } from '../lib/types';
import { validateMediaSlotId } from '../lib/validators';
import { IMAGE_TARGETS } from '../lib/image';
import { releaseStaged, stageImage, type StagedImage } from '../lib/staging';
import { PreviewModal } from '../features/preview/PreviewModal';
import { mediaSlotPath } from '../lib/storagePaths';
import { deleteMedia, mediaUrl, uploadMedia } from '../lib/storage';
import { friendlyErrorMessage } from '../lib/errors';
import { useToast } from '../components/Toast';
import { Button } from '../components/Button';
import { Dropzone } from '../components/Dropzone';
import { Spinner } from '../components/Spinner';
import { TextField } from '../components/Field';

function SlotCard({ slot, onChanged }: { slot: MediaSlotRow; onChanged: () => void }) {
  const toast = useToast();
  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [staged, setStaged] = useState<StagedImage | null>(null);
  const [viewing, setViewing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  async function handleUpload(file: File) {
    setCompressing(true);
    try {
      setStaged(await stageImage(file, IMAGE_TARGETS.cover));
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setCompressing(false);
    }
  }

  function cancel() {
    releaseStaged(staged);
    setStaged(null);
  }

  async function confirm() {
    if (!staged) return;
    setUploading(true);
    try {
      const path = mediaSlotPath(slot.slot, staged.ext);
      await uploadMedia(path, staged.blob, staged.mime);
      const oldPath = slot.path;
      await updateSlot(slot.slot, { path, focal_x: 0.5, focal_y: 0.5 });
      if (oldPath) await deleteMedia([oldPath]);
      cancel();
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    } finally {
      setUploading(false);
    }
  }

  async function handleClearPhoto() {
    try {
      const oldPath = slot.path;
      await updateSlot(slot.slot, { path: null });
      if (oldPath) await deleteMedia([oldPath]);
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  async function handleSetFocal(e: MouseEvent<HTMLDivElement>) {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    try {
      await updateSlot(slot.slot, { focal_x: Number(x.toFixed(3)), focal_y: Number(y.toFixed(3)) });
      onChanged();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  const url = mediaUrl(slot.path);

  return (
    <div className="card equipment-card">
      <strong>{slot.label || slot.slot}</strong>
      <span className="muted">{slot.slot}</span>
      {url ? (
        <div className="focal-image-wrap" onClick={handleSetFocal}>
          <img ref={imgRef} src={url} alt={slot.label} />
          <span
            className="focal-dot"
            style={{ left: `${slot.focal_x * 100}%`, top: `${slot.focal_y * 100}%` }}
          />
        </div>
      ) : (
        <div className="thumb-placeholder" />
      )}
      <Dropzone
        accept="image/jpeg,image/png,image/webp,image/heic"
        label={compressing ? 'Compressing…' : 'Drop or click to upload'}
        onFile={handleUpload}
        disabled={compressing || uploading}
      />
      {url ? (
        <div className="row">
          <Button tone="tonal" size="sm" onClick={() => setViewing(true)} style={{ minWidth: 0 }}>
            Preview
          </Button>
          <Button tone="neutral" size="sm" onClick={handleClearPhoto} style={{ minWidth: 0 }}>
            Clear
          </Button>
        </div>
      ) : null}
      {url ? <span className="muted">Click the image to set the focal point.</span> : null}
      {staged ? (
        <PreviewModal
          subject={{ kind: 'cover', slot: slot.slot, url: staged.url, focalX: 0.5, focalY: 0.5 }}
          stats={{ originalBytes: staged.originalBytes, finalBytes: staged.blob.size }}
          onConfirm={confirm}
          confirming={uploading}
          onClose={cancel}
        />
      ) : null}
      {viewing && url ? <PreviewModal subject={{ kind: 'cover', slot: slot.slot, url, focalX: Number(slot.focal_x), focalY: Number(slot.focal_y) }} onClose={() => setViewing(false)} /> : null}
    </div>
  );
}

export function CoversPage() {
  const toast = useToast();
  const [slots, setSlots] = useState<MediaSlotRow[] | null>(null);
  const [newSlot, setNewSlot] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [slotError, setSlotError] = useState<string | null>(null);

  function refresh() {
    listSlots().then(setSlots).catch(() => setSlots([]));
  }

  useEffect(refresh, []);

  async function handleAddSlot(e: FormEvent) {
    e.preventDefault();
    const err = validateMediaSlotId(newSlot);
    setSlotError(err);
    if (err) return;
    try {
      await createSlot(newSlot, newLabel || newSlot);
      setNewSlot('');
      setNewLabel('');
      toast.show('Slot added', 'success');
      refresh();
    } catch (e) {
      toast.show(friendlyErrorMessage(e), 'error');
    }
  }

  return (
    <div>
      <div className="topbar">
        <h1 className="page-title">Covers</h1>
      </div>

      <form className="card" style={{ marginBottom: 24, maxWidth: 560 }} onSubmit={handleAddSlot}>
        <h3 style={{ marginBottom: 12 }}>Add a custom slot</h3>
        <div className="row">
          <TextField label="Slot id" placeholder="cover.goal.custom" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} error={slotError} />
          <TextField label="Label" placeholder="Goal: custom" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
        </div>
        <Button type="submit" tone="tonal">
          Add slot
        </Button>
      </form>

      {slots === null ? (
        <Spinner />
      ) : (
        <div className="media-grid">
          {slots.map((s) => (
            <SlotCard key={s.slot} slot={s} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}
